const en = {
    seoDescription: 'Ura Advisory is an AI-powered tax, accounting, payroll and CFO firm in Prishtina, Kosovo, led by a former ATK tax inspector, serving clients in Kosovo, the UK, the US, Germany, Switzerland and Austria.',
    hero: {
        eyebrow: 'AI-Powered Tax, Finance and Business Transformation',
        titleLead: 'Tax and finance expertise forged in Kosovo,',
        titleEmphasis: 'engineered for growth.',
        text: 'Ura Advisory brings experience from inside the Kosovo Tax Administration and EY together with a client portal that reads your documents, tracks every deadline and keeps your numbers current. For companies in Kosovo and businesses abroad that want finance done properly.',
        secondary: 'Explore the client portal',
        facts: ['Prishtina, Kosovo', 'Working in CET', 'English and Albanian']
    },
    credentials: {
        label: "The founder's experience behind every engagement",
        items: [
            { title: 'Former ATK tax inspector', text: 'Tax Audit department, Kosovo Tax Administration' },
            { title: 'Tax and finance at EY', text: 'Senior Tax and Finance Consultant, EY Kosovo' },
            { title: 'CFO, international technology company', text: 'FP&A, investor reporting, payroll and HR' },
            { title: 'Management consultant', text: 'Strategy, structure and operations' }
        ],
        standardsLabel: 'Standards we work to',
        standards: ['IFRS', 'GDPR', 'ATK EDI', 'Kosovo tax law']
    },
    positioning: {
        eyebrow: 'Why Ura',
        statement: 'Most firms either know the rules or build the systems. We do both, so your filings are right, your numbers are current and every decision comes with its tax effect already known.',
        pillars: [
            {
                title: 'Judgement from inside the system',
                text: 'Our founder examined company accounts as an ATK inspector. We prepare your file the way an inspector will read it, and we put our positions in writing.'
            },
            {
                title: 'Systems that do the routine',
                text: 'Documents are read, categorised and exported automatically, so our people spend their time on review and advice instead of data entry.'
            },
            {
                title: 'One accountable lead',
                text: 'A named lead knows your file, answers within one working day and signs off every filing before it leaves.'
            }
        ]
    },
    practice: {
        eyebrow: 'Practice areas',
        title: 'Six practice areas, one accountable team',
        text: 'From the monthly VAT return to a group restructuring, each practice runs on the same calendar, the same portal and the same standard of review.',
        explore: 'Explore',
        featured: 'Featured services',
        allServices: 'All services'
    },
    platform: {
        eyebrow: 'Client portal',
        title: 'One portal for every number',
        text: 'Your documents, filings, payments, deadlines and questions live in one secure place, shared with the team that does the work.',
        cta: 'Take the full portal tour',
        tabsLabel: 'Portal features',
        tabs: [
            {
                id: 'documents',
                label: 'Documents',
                title: 'Upload once, read automatically',
                text: 'Send invoices, receipts, contracts and statements as PDF, JPG, PNG, Excel or Word. Each file is read, categorised and queued for review by your accountant.',
                points: ['Supplier, invoice number, date, VAT, total, currency and payment terms', 'Categories from operational costs to rent and utilities', 'A status for every file, from reading to approved']
            },
            {
                id: 'exports',
                label: 'Exports',
                title: 'Excel books in the structure ATK expects',
                text: 'Approved documents flow into Excel exports structured for the ATK VAT purchase and sales books, with registers for payroll and corporate tax.',
                points: ['Purchase and sales books for each VAT period', 'Totals that reconcile to the return', 'Download any period, at any time']
            },
            {
                id: 'payments',
                label: 'Payments',
                title: 'Payment slips before every due date',
                text: 'For each tax or fee due, a payment slip appears in the portal. Download it, mark it as paid and upload the bank confirmation, and we see it at once.',
                points: ['Slips for VAT, wage tax, pension and corporate tax', 'Mark as paid and attach the confirmation', 'Overdue items flagged automatically']
            },
            {
                id: 'calendar',
                label: 'Calendar',
                title: 'Every deadline, with its status',
                text: 'The compliance calendar is built from your registrations: VAT by the 20th, payroll and pension by the 15th, quarterly corporate tax advances and the annual return by 31 March.',
                points: ['Status from upcoming to filed and paid', 'Reminders ahead of each deadline', 'Contract renewals and obligations alongside']
            },
            {
                id: 'assistant',
                label: 'Assistant',
                title: 'Ask about your own numbers',
                text: 'Each client has an AI assistant that answers questions from your own approved records and shows its sources. When it is not sure, the question goes to your accountant.',
                points: ['Answers based on your approved documents and filings', 'Sources shown with every answer', 'Questions it cannot answer are forwarded to people']
            }
        ]
    },
    demo: {
        eyebrow: 'AI document reading',
        title: 'From a supplier invoice to a line in your VAT book',
        text: 'The portal reads each document, suggests a category and an account, and checks the VAT against the 18% and 8% rates. An accountant reviews every reading before it reaches your books.',
        points: ['Reads PDF, JPG, PNG, Excel and Word files', 'Flags VAT that matches neither rate', 'Nothing is filed without a person checking it'],
        link: 'How we use AI'
    },
    markets: {
        eyebrow: 'Markets',
        title: 'From Prishtina to wherever you do business',
        text: 'We work in English and Albanian, in the Central European time zone, for companies in Kosovo and for businesses abroad that need a finance team they can rely on.',
        items: [
            {
                code: 'XK',
                name: 'Kosovo',
                text: 'Full tax, accounting, payroll and advisory for local companies, with returns filed through ATK EDI.',
                points: ['Monthly VAT, payroll and pension filings', 'IFRS financial statements', 'ATK audits and disputes']
            },
            {
                code: 'GB',
                name: 'United Kingdom',
                text: 'Offshore accounting and back-office support for UK companies and accounting practices, in your software and under your direction.',
                points: ['Bookkeeping and month-end close in Xero or QuickBooks', 'Management reporting', 'Kosovo entities of UK groups']
            },
            {
                code: 'US',
                name: 'United States',
                text: 'Accounting, FP&A and fractional CFO support for US companies, with working hours that overlap the US East Coast morning.',
                points: ['Bookkeeping in QuickBooks or Xero', 'Budgeting, forecasting and investor reporting', 'Kosovo subsidiaries and teams']
            },
            {
                code: 'DE',
                name: 'Germany',
                text: 'Accounting support for German companies and their Kosovo subsidiaries, in DATEV or your own system, in the same time zone.',
                points: ['Kosovo subsidiaries of German groups', 'Intercompany and transfer pricing documentation', 'Diaspora entrepreneurs investing at home']
            },
            {
                code: 'CH',
                name: 'Switzerland',
                text: 'A finance back office for Swiss companies with teams or subsidiaries in Kosovo, including diaspora-owned businesses.',
                points: ['Monthly close and reporting to head office', 'Payroll for Kosovo teams', 'Market entry and company set-up']
            },
            {
                code: 'AT',
                name: 'Austria',
                text: 'Support for Austrian companies investing in Kosovo and for entrepreneurs bringing a business home.',
                points: ['Set-up, registration and first filings', 'Accounting and payroll', 'Cross-border tax questions']
            },
            {
                code: '',
                name: 'Anywhere',
                text: 'Remote finance teams and Kosovo market entry for companies from any country.',
                points: ['A virtual finance office', 'Setting up in Kosovo', 'Work in English, in your tools']
            }
        ],
        imageAlt: 'Glass facade of a modern office tower against a blue sky',
        imageTitle: 'Senior finance capacity, one time zone away',
        facts: [
            { title: 'Central European Time', text: 'The same hours as Berlin, Vienna and Zurich, one hour ahead of London.' },
            { title: 'English and Albanian', text: 'Reports, calls and correspondence in the language your team works in.' },
            { title: 'Foreign investors', text: 'Setting up in Kosovo, from the legal form to the first VAT return.' }
        ],
        investorsLink: 'Setting up in Kosovo'
    },
    process: {
        eyebrow: 'How we work',
        title: 'Four steps from the first call to finances you can rely on',
        text: 'Every engagement follows the same path, so you always know what happens next and who is responsible.',
        steps: [
            { title: 'Diagnose', description: 'A free scoping call, then a review of your registrations, filings and records. You receive a written view of risks and priorities.' },
            { title: 'Onboard', description: 'Scope and fees go into an engagement letter. We set up your portal, verify your company and take over your records.' },
            { title: 'Run', description: 'Each month we process documents, prepare filings, issue payment slips and close the books on a fixed calendar.' },
            { title: 'Advise', description: 'Quarterly reviews, management reports and advice before decisions are made, not after.' }
        ]
    },
    industries: {
        eyebrow: 'Industries',
        title: 'Built around how your sector works',
        text: 'VAT on construction contracts, payroll for fast-growing tech teams, stock for manufacturers, marketplaces for online sellers. We start from the issues that matter in your industry.',
        cta: 'See all industries'
    },
    founder: {
        eyebrow: "Founder's statement",
        statement: [
            'I worked inside the Kosovo Tax Administration examining company accounts, and then at EY preparing them. From both sides I saw the same thing: most tax problems start months earlier, in records nobody could see clearly.',
            'I started Ura Advisory to be the bridge between the two. Senior tax and finance judgement, written down and defensible, with systems that keep every document, deadline and number in view for our clients in Kosovo and abroad.'
        ],
        name: 'Qëndrim Uka',
        role: 'Founder, Ura Advisory',
        chips: ['Former ATK tax inspector', 'EY Kosovo', 'CFO', 'BSc Accounting, GPA 9.93'],
        link: 'Read the founder profile'
    },
    insights: {
        eyebrow: 'Insights',
        title: 'Latest analysis',
        text: 'Deadlines, rule changes and practical guides for businesses in Kosovo and abroad.',
        cta: 'View all insights'
    },
    testimonials: {
        eyebrow: 'Clients',
        title: 'In their words'
    },
    faq: {
        eyebrow: 'FAQ',
        title: 'Questions we hear first',
        items: [
            {
                question: 'Who is Ura Advisory for?',
                answer: 'Companies in Kosovo that want their tax, accounting and payroll handled properly, and businesses abroad, mainly in the United Kingdom, the United States, Germany, Switzerland and Austria, that need a finance team in Kosovo or are setting up here. We work with startups, SMEs, technology companies and foreign investors.'
            },
            {
                question: 'Can you work with companies outside Kosovo?',
                answer: 'Yes. We work in English, in the Central European time zone, and in your own software where needed. For filings in another country, we work alongside your local adviser and under your direction.'
            },
            {
                question: 'How does the client portal use AI?',
                answer: 'The portal reads the documents you upload, such as the supplier, invoice number, date, VAT and total, suggests a category and checks the VAT rate. An accountant reviews every reading before it is used in your books or in a filing. The assistant answers questions from your own approved records.'
            },
            {
                question: 'Is our data safe?',
                answer: 'Documents are stored privately and opened only through links that expire. Accounts support two-factor authentication, access follows roles, and every important action is logged. Your data is never used to train AI models.'
            },
            {
                question: 'What does it cost?',
                answer: 'Monthly packages start from €190 for startups and small businesses, €490 for growing SMEs and €1,200 for multi-entity groups. Final quotes follow a free scoping call. Prices exclude VAT where applicable.'
            },
            {
                question: 'How quickly can we start?',
                answer: 'After the scoping call we send a written proposal. Once the engagement letter is signed and your company is verified in the portal, we take over from the next filing cycle.'
            }
        ]
    }
}

const sq: typeof en = {
    seoDescription: 'Ura Advisory është firmë tatimore, kontabël, e pagave dhe CFO e fuqizuar nga AI në Prishtinë, e udhëhequr nga një ish-inspektor tatimor i ATK-së, për klientë në Kosovë, MB, SHBA, Gjermani, Zvicër dhe Austri.',
    hero: {
        eyebrow: 'Tatime, financa dhe transformim biznesi me AI',
        titleLead: 'Ekspertizë tatimore dhe financiare e kalitur në Kosovë,',
        titleEmphasis: 'e ndërtuar për rritje.',
        text: 'Ura Advisory bashkon përvojën nga brenda Administratës Tatimore të Kosovës dhe nga EY me një portal klienti që i lexon dokumentet tuaja, ndjek çdo afat dhe i mban shifrat të përditësuara. Për kompanitë në Kosovë dhe bizneset jashtë saj që duan financa të rregullta.',
        secondary: 'Njihuni me portalin e klientit',
        facts: ['Prishtinë, Kosovë', 'Punojmë sipas orës CET', 'Anglisht dhe shqip']
    },
    credentials: {
        label: 'Përvoja e themeluesit në çdo angazhim',
        items: [
            { title: 'Ish-inspektor tatimor i ATK-së', text: 'Departamenti i Kontrollit Tatimor, Administrata Tatimore e Kosovës' },
            { title: 'Tatime dhe financa në EY', text: 'Konsulent i lartë për tatime dhe financa, EY Kosovë' },
            { title: 'CFO në një kompani ndërkombëtare teknologjike', text: 'FP&A, raportim për investitorët, paga dhe burime njerëzore' },
            { title: 'Konsulent menaxhmenti', text: 'Strategji, strukturë dhe operacione' }
        ],
        standardsLabel: 'Standardet që ndjekim',
        standards: ['SNRF (IFRS)', 'GDPR', 'EDI i ATK-së', 'Legjislacioni tatimor i Kosovës']
    },
    positioning: {
        eyebrow: 'Pse Ura',
        statement: 'Shumica e firmave ose i njohin rregullat, ose ndërtojnë sistemet. Ne i bëjmë të dyja, që deklaratat tuaja të jenë të sakta, shifrat të përditësuara dhe çdo vendim të merret duke e ditur paraprakisht efektin tatimor.',
        pillars: [
            {
                title: 'Gjykim nga brenda sistemit',
                text: 'Themeluesi ynë ka kontrolluar llogaritë e kompanive si inspektor i ATK-së. Dosjen tuaj e përgatisim ashtu siç do ta lexojë një inspektor dhe qëndrimet tona i japim me shkrim.'
            },
            {
                title: 'Sisteme që kryejnë rutinën',
                text: 'Dokumentet lexohen, kategorizohen dhe eksportohen automatikisht, që njerëzit tanë ta kalojnë kohën te rishikimi dhe këshillimi, jo te futja e të dhënave.'
            },
            {
                title: 'Një përgjegjës i vetëm',
                text: 'Një person i caktuar e njeh dosjen tuaj, përgjigjet brenda një dite pune dhe e miraton çdo deklaratë para se të dorëzohet.'
            }
        ]
    },
    practice: {
        eyebrow: 'Fushat e ekspertizës',
        title: 'Gjashtë fusha ekspertize, një ekip përgjegjës',
        text: 'Nga deklarata mujore e TVSH-së te ristrukturimi i një grupi, çdo fushë punon me të njëjtin kalendar, të njëjtin portal dhe të njëjtin standard rishikimi.',
        explore: 'Shiko',
        featured: 'Shërbime në fokus',
        allServices: 'Të gjitha shërbimet'
    },
    platform: {
        eyebrow: 'Portali i klientit',
        title: 'Një portal për çdo shifër',
        text: 'Dokumentet, deklaratat, pagesat, afatet dhe pyetjet tuaja ndodhen në një vend të sigurt, të përbashkët me ekipin që e kryen punën.',
        cta: 'Shiko turin e plotë të portalit',
        tabsLabel: 'Veçoritë e portalit',
        tabs: [
            {
                id: 'documents',
                label: 'Dokumentet',
                title: 'Ngarkoni një herë, lexohen automatikisht',
                text: 'Dërgoni fatura, kuponë, kontrata dhe pasqyra si PDF, JPG, PNG, Excel ose Word. Çdo skedar lexohet, kategorizohet dhe pret rishikimin nga kontabilisti juaj.',
                points: ['Furnitori, numri i faturës, data, TVSH-ja, totali, valuta dhe kushtet e pagesës', 'Kategori nga shpenzimet operative te qiraja dhe shërbimet komunale', 'Status për çdo skedar, nga leximi te miratimi']
            },
            {
                id: 'exports',
                label: 'Eksportet',
                title: 'Libra në Excel sipas strukturës që kërkon ATK',
                text: 'Dokumentet e miratuara kalojnë në eksporte Excel të strukturuara për librin e blerjeve dhe librin e shitjeve të TVSH-së sipas ATK-së, bashkë me regjistra për pagat dhe tatimin në korporata.',
                points: ['Libri i blerjeve dhe i shitjeve për çdo periudhë të TVSH-së', 'Totale që përputhen me deklaratën', 'Shkarkoni çdo periudhë, në çdo kohë']
            },
            {
                id: 'payments',
                label: 'Pagesat',
                title: 'Fletëpagesa para çdo afati',
                text: 'Për çdo tatim ose tarifë në afat, në portal shfaqet një fletëpagesë. E shkarkoni, e shënoni si të paguar dhe ngarkoni konfirmimin e bankës, e ne e shohim menjëherë.',
                points: ['Fletëpagesa për TVSH-në, tatimin në paga, pensionin dhe tatimin në korporata', 'Shënoni pagesën dhe bashkëngjitni konfirmimin', 'Pagesat me vonesë sinjalizohen automatikisht']
            },
            {
                id: 'calendar',
                label: 'Kalendari',
                title: 'Çdo afat, me statusin e tij',
                text: 'Kalendari i përputhshmërisë ndërtohet nga regjistrimet tuaja: TVSH deri më 20, pagat dhe pensionet deri më 15, paradhëniet tremujore të tatimit në korporata dhe deklarata vjetore deri më 31 mars.',
                points: ['Status nga i ardhshëm te i dorëzuar dhe i paguar', 'Kujtesa para çdo afati', 'Rinovimet e kontratave dhe detyrimet në të njëjtin vend']
            },
            {
                id: 'assistant',
                label: 'Asistenti',
                title: 'Pyetni për shifrat tuaja',
                text: 'Çdo klient ka një asistent AI që u përgjigjet pyetjeve nga të dhënat tuaja të miratuara dhe tregon burimet. Kur nuk është i sigurt, pyetja i kalon kontabilistit tuaj.',
                points: ['Përgjigje të bazuara në dokumentet dhe deklaratat tuaja të miratuara', 'Burimet shfaqen me çdo përgjigje', 'Pyetjet pa përgjigje u kalojnë njerëzve']
            }
        ]
    },
    demo: {
        eyebrow: 'Leximi i dokumenteve me AI',
        title: 'Nga fatura e furnitorit te një rresht në librin tuaj të TVSH-së',
        text: 'Portali e lexon çdo dokument, sugjeron kategorinë dhe llogarinë dhe e kontrollon TVSH-në sipas normave 18% dhe 8%. Kontabilisti e rishikon çdo lexim para se të hyjë në librat tuaj.',
        points: ['Lexon skedarë PDF, JPG, PNG, Excel dhe Word', 'Sinjalizon TVSH-në që nuk përputhet me asnjë normë', 'Asgjë nuk dorëzohet pa e kontrolluar një person'],
        link: 'Si e përdorim AI-në'
    },
    markets: {
        eyebrow: 'Tregjet',
        title: 'Nga Prishtina deri atje ku bëni biznes',
        text: 'Punojmë në anglisht dhe shqip, sipas zonës kohore të Evropës Qendrore, për kompanitë në Kosovë dhe për bizneset jashtë vendit që kanë nevojë për një ekip financiar të besueshëm.',
        items: [
            {
                code: 'XK',
                name: 'Kosova',
                text: 'Shërbim i plotë tatimor, kontabël, i pagave dhe këshillimor për kompanitë vendase, me deklarata të dorëzuara përmes EDI të ATK-së.',
                points: ['Deklarimet mujore të TVSH-së, pagave dhe pensioneve', 'Pasqyra financiare sipas SNRF', 'Kontrolle dhe kontestime me ATK-në']
            },
            {
                code: 'GB',
                name: 'Mbretëria e Bashkuar',
                text: 'Kontabilitet offshore dhe mbështetje back office për kompani dhe zyra kontabiliteti britanike, në softuerin tuaj dhe sipas udhëzimeve tuaja.',
                points: ['Regjistrime dhe mbyllje mujore në Xero ose QuickBooks', 'Raportim menaxherial', 'Njësi kosovare të grupeve britanike']
            },
            {
                code: 'US',
                name: 'Shtetet e Bashkuara',
                text: 'Kontabilitet, FP&A dhe CFO me kohë të pjesshme për kompani amerikane, me orar që mbulon edhe mëngjesin e Bregut Lindor të SHBA-së.',
                points: ['Regjistrime në QuickBooks ose Xero', 'Buxhetim, parashikim dhe raportim për investitorët', 'Filiale dhe ekipe në Kosovë']
            },
            {
                code: 'DE',
                name: 'Gjermania',
                text: 'Mbështetje kontabël për kompani gjermane dhe filialet e tyre në Kosovë, në DATEV ose në sistemin tuaj, në të njëjtën zonë kohore.',
                points: ['Filiale kosovare të grupeve gjermane', 'Transaksione brenda grupit dhe dokumentim i çmimeve të transferimit', 'Sipërmarrës të diasporës që investojnë në vendlindje']
            },
            {
                code: 'CH',
                name: 'Zvicra',
                text: 'Back office financiar për kompani zvicerane me ekipe ose filiale në Kosovë, përfshirë bizneset në pronësi të diasporës.',
                points: ['Mbyllje mujore dhe raportim për zyrën qendrore', 'Paga për ekipet në Kosovë', 'Hyrje në treg dhe themelim kompanie']
            },
            {
                code: 'AT',
                name: 'Austria',
                text: 'Mbështetje për kompani austriake që investojnë në Kosovë dhe për sipërmarrës që e sjellin biznesin në vendlindje.',
                points: ['Themelim, regjistrim dhe deklaratat e para', 'Kontabilitet dhe paga', 'Pyetje tatimore ndërkufitare']
            },
            {
                code: '',
                name: 'Kudo',
                text: 'Ekipe financiare në distancë dhe hyrje në tregun e Kosovës për kompani nga çdo vend.',
                points: ['Zyrë financiare virtuale', 'Themelimi i biznesit në Kosovë', 'Punë në anglisht, në mjetet tuaja']
            }
        ],
        imageAlt: 'Fasadë xhami e një kulle moderne zyrash nën qiell të kaltër',
        imageTitle: 'Kapacitet financiar i nivelit të lartë, në zonën tuaj kohore',
        facts: [
            { title: 'Ora e Evropës Qendrore', text: 'Të njëjtat orë si Berlini, Vjena dhe Cyrihu, një orë përpara Londrës.' },
            { title: 'Anglisht dhe shqip', text: 'Raporte, takime dhe korrespondencë në gjuhën në të cilën punon ekipi juaj.' },
            { title: 'Investitorët e huaj', text: 'Themelimi i biznesit në Kosovë, nga forma juridike te deklarata e parë e TVSH-së.' }
        ],
        investorsLink: 'Themelimi në Kosovë'
    },
    process: {
        eyebrow: 'Si punojmë',
        title: 'Katër hapa nga biseda e parë te financa mbi të cilat mund të mbështeteni',
        text: 'Çdo angazhim ndjek të njëjtën rrugë, që ta dini gjithmonë çfarë vjen më pas dhe kush është përgjegjës.',
        steps: [
            { title: 'Diagnostikim', description: 'Një bisedë falas për fushëveprimin, pastaj rishikim i regjistrimeve, deklaratave dhe regjistrave tuaj. Merrni me shkrim pasqyrën e rreziqeve dhe të prioriteteve.' },
            { title: 'Nisje', description: 'Fushëveprimi dhe tarifat shkruhen në letrën e angazhimit. Konfigurojmë portalin, verifikojmë kompaninë tuaj dhe i marrim përsipër regjistrat.' },
            { title: 'Realizim', description: 'Çdo muaj përpunojmë dokumentet, përgatisim deklaratat, lëshojmë fletëpagesat dhe i mbyllim librat sipas një kalendari fiks.' },
            { title: 'Këshillim', description: 'Rishikime tremujore, raporte menaxheriale dhe këshilla para se të merren vendimet, jo pas tyre.' }
        ]
    },
    industries: {
        eyebrow: 'Industritë',
        title: 'Të ndërtuara sipas mënyrës si punon sektori juaj',
        text: 'TVSH në kontratat e ndërtimit, paga për ekipe teknologjike që rriten shpejt, stoqe për prodhuesit, platforma për shitësit online. Nisemi nga çështjet që kanë peshë në industrinë tuaj.',
        cta: 'Shiko të gjitha industritë'
    },
    founder: {
        eyebrow: 'Deklarata e themeluesit',
        statement: [
            'Kam punuar brenda Administratës Tatimore të Kosovës duke kontrolluar llogaritë e kompanive, e më pas në EY duke i përgatitur ato. Nga të dyja anët pashë të njëjtën gjë: shumica e problemeve tatimore nisin muaj më herët, në regjistra që askush nuk i shihte qartë.',
            'Ura Advisory e themelova për të qenë ura mes këtyre dy anëve. Gjykim i nivelit të lartë tatimor dhe financiar, i shkruar dhe i mbrojtshëm, me sisteme që ua mbajnë para syve çdo dokument, afat dhe shifër klientëve tanë në Kosovë dhe jashtë saj.'
        ],
        name: 'Qëndrim Uka',
        role: 'Themelues, Ura Advisory',
        chips: ['Ish-inspektor tatimor i ATK-së', 'EY Kosovë', 'CFO', 'Bachelor në Kontabilitet, mesatarja 9,93'],
        link: 'Lexoni profilin e themeluesit'
    },
    insights: {
        eyebrow: 'Analiza',
        title: 'Analizat më të fundit',
        text: 'Afate, ndryshime rregullash dhe udhëzues praktikë për bizneset në Kosovë dhe jashtë saj.',
        cta: 'Shiko të gjitha analizat'
    },
    testimonials: {
        eyebrow: 'Klientët',
        title: 'Me fjalët e tyre'
    },
    faq: {
        eyebrow: 'Pyetje të shpeshta',
        title: 'Pyetjet që na bëhen më shpesh',
        items: [
            {
                question: 'Për kë është Ura Advisory?',
                answer: 'Për kompanitë në Kosovë që duan që tatimet, kontabiliteti dhe pagat të trajtohen si duhet, dhe për bizneset jashtë vendit, kryesisht në Mbretërinë e Bashkuar, SHBA, Gjermani, Zvicër dhe Austri, që kanë nevojë për një ekip financiar në Kosovë ose që po themelojnë biznes këtu. Punojmë me startup-e, NVM, kompani teknologjike dhe investitorë të huaj.'
            },
            {
                question: 'A punoni me kompani jashtë Kosovës?',
                answer: 'Po. Punojmë në anglisht, sipas zonës kohore të Evropës Qendrore dhe, kur duhet, në softuerin tuaj. Për deklarimet në një vend tjetër, punojmë bashkë me këshilltarin tuaj vendor dhe sipas udhëzimeve tuaja.'
            },
            {
                question: 'Si e përdor AI-në portali i klientit?',
                answer: 'Portali i lexon dokumentet që ngarkoni, si furnitorin, numrin e faturës, datën, TVSH-në dhe totalin, sugjeron kategorinë dhe kontrollon normën e TVSH-së. Kontabilisti e rishikon çdo lexim para se të përdoret në librat tuaj ose në një deklaratë. Asistenti u përgjigjet pyetjeve nga të dhënat tuaja të miratuara.'
            },
            {
                question: 'A janë të sigurta të dhënat tona?',
                answer: 'Dokumentet ruhen në mënyrë private dhe hapen vetëm përmes lidhjeve që skadojnë. Llogaritë mbështesin vërtetimin me dy faktorë, qasja ndjek rolet dhe çdo veprim i rëndësishëm regjistrohet. Të dhënat tuaja nuk përdoren kurrë për të trajnuar modele AI.'
            },
            {
                question: 'Sa kushton?',
                answer: 'Paketat mujore fillojnë nga 190 € për startup-e dhe biznese të vogla, 490 € për NVM në rritje dhe 1.200 € për grupe me disa njësi. Oferta përfundimtare vjen pas një bisede falas për fushëveprimin. Çmimet nuk e përfshijnë TVSH-në, aty ku aplikohet.'
            },
            {
                question: 'Sa shpejt mund të fillojmë?',
                answer: 'Pas bisedës për fushëveprimin ju dërgojmë një ofertë me shkrim. Sapo të nënshkruhet letra e angazhimit dhe kompania juaj të verifikohet në portal, e marrim përsipër punën nga cikli i ardhshëm i deklarimeve.'
            }
        ]
    }
}

export const siteHome = { en, sq }
